<template>
  <teleport to="body">
    <transition-group name="snack-bar" tag="div" class="snack-bar-notifications">
      <div
        v-for="notification in snackBarNotifications"
        :key="notification.id"
        class="generic-snack-bar"
        :class="notification.type && `generic-snack-bar--${notification.type}`"
        :role="notification.actionLabel || notification.dismissible ? 'button' : 'status'"
        :tabindex="notification.actionLabel || notification.dismissible ? 0 : undefined"
        @click="handleClick(notification)"
        @keydown.enter.prevent="handleClick(notification)"
        @keydown.space.prevent="handleClick(notification)"
        @pointerenter="pauseSnackBarNotification(notification)"
        @pointerleave="resumePausedSnackBarNotification(notification)"
      >
        <span class="generic-snack-bar__message">{{ notification.message }}</span>
        <span v-if="notification.actionLabel" class="generic-snack-bar__action-link">{{
          notification.actionLabel
        }}</span>
      </div>
    </transition-group>
  </teleport>
</template>

<script setup lang="ts">
import {
  dismissSnackBarNotification,
  pauseSnackBarNotification,
  resumePausedSnackBarNotification,
  snackBarNotifications,
  type SnackBarNotification,
} from '@lib/notifications';

function handleClick(notification: SnackBarNotification) {
  if (notification.onAction) {
    notification.onAction();
    dismissSnackBarNotification(notification.id);
  } else if (notification.dismissible) {
    dismissSnackBarNotification(notification.id);
  }
}
</script>

<style scoped lang="scss">
@import '@styles/variables';

.snack-bar-notifications {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 1.2rem 2rem;
  z-index: $zi-snackbar;
  gap: 0.6rem;
  pointer-events: none;
}

:global(body:has([data-floating-actions] .icon-button) .snack-bar-notifications) {
  padding-bottom: 8rem;
}

.generic-snack-bar {
  --snack-accent: #{$c-dark-lightened-50};
  --snack-wash: transparent;

  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0;
  max-width: min(44rem, 80vw);
  align-self: center;
  padding: 0.9rem 1.2rem;
  border-radius: 0.9rem;
  font:
    400 1.4rem/1.9rem 'Lato',
    sans-serif;
  color: $c-dark;
  background: linear-gradient(var(--snack-wash), var(--snack-wash)), #fff;
  box-shadow:
    0 0.4rem 1.6rem rgba($c-dark, 0.14),
    inset 0 0 0 1px rgba($c-dark, 0.07);
  pointer-events: auto;
  cursor: pointer;

  &::before {
    content: '';
    flex: none;
    width: 0.6rem;
    height: 0.6rem;
    border-radius: 50%;
    background: var(--snack-accent);
  }

  &--success {
    --snack-accent: #{$c-success};
    --snack-wash: #{rgba($c-success, 0.1)};
  }

  &--warning {
    --snack-accent: #{$c-warning};
    --snack-wash: #{rgba($c-warning, 0.12)};
  }

  &--error {
    --snack-accent: #{$c-error};
    --snack-wash: #{rgba($c-error, 0.08)};
  }

  &__action-link {
    flex: none;
    margin-left: auto;
    padding: 0.2rem 0.8rem;
    border-radius: 0.5rem;
    font:
      600 1.3rem/1.8rem 'Lato',
      sans-serif;
    text-transform: none;
    color: $c-dark;
    text-decoration: none;
    background: rgba($c-dark, 0.08);
    transition:
      background-color 0.15s ease,
      transform 0.1s ease;
  }

  &:hover &__action-link {
    background: rgba($c-dark, 0.16);
  }

  &:active &__action-link {
    transform: scale(0.96);
  }
}

.snack-bar-enter-active,
.snack-bar-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.snack-bar-enter-from,
.snack-bar-leave-to {
  opacity: 0;
  transform: translateY(1rem);
}

@media (prefers-reduced-motion: reduce) {
  .generic-snack-bar__action-link,
  .snack-bar-enter-active,
  .snack-bar-leave-active {
    transition: none;
  }
}
</style>
